import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, ArrowRight, Loader2, ShieldCheck, Check } from "lucide-react";
import { api, apiError, track } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEP_LABELS = ["Package", "Your Details", "Review", "Payment"];

export default function Checkout() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [step, setStep] = useState(0);
  const [selectedSlug, setSelectedSlug] = useState(params.get("package") || "");
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", date_of_birth: "", birth_time: "", birth_place: "",
    language: "English", methodology: "pythagorean", areas_of_interest: "", alternate_spelling: "",
    comparison_objective: "neutral", comparison_target_number: null,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    track("checkout_started");
    api.get("/packages").then((r) => {
      setPackages(r.data);
      if (r.data[0]) setSelectedSlug((current) => current || r.data[0].slug);
    }).catch(() => {});
  }, []);

  const pkg = useMemo(() => packages.find((p) => p.slug === selectedSlug), [packages, selectedSlug]);
  const discount = couponApplied ? Math.round((pkg?.price_inr || 0) * couponApplied.percent_off / 100 * 100) / 100 : 0;
  const finalAmount = Math.max((pkg?.price_inr || 0) - discount, 1);
  const isName = selectedSlug === "name";

  const validateDetails = () => {
    if (!form.full_name.trim()) return "Please enter your full name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email";
    if (!form.date_of_birth) return "Please enter your date of birth";
    if (new Date(form.date_of_birth) > new Date()) return "Date of birth cannot be in the future";
    if (isName && form.comparison_objective === "number" &&
      ![1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33].includes(form.comparison_target_number)) {
      return "Choose a number from 1–9, 11, 22, or 33";
    }
    return null;
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon });
      setCouponApplied(data); toast.success(`Coupon applied: ${data.percent_off}% off`);
    } catch (err) { setCouponApplied(null); toast.error(apiError(err)); }
  };

  const next = () => {
    if (step === 0 && !pkg) { toast.error("Please select a package"); return; }
    if (step === 1) { const e = validateDetails(); if (e) { toast.error(e); return; } }
    if (step === 2 && !terms) { toast.error("Please accept the terms to continue"); return; }
    if (step === 2) { createOrder(); return; }
    setStep((s) => s + 1);
  };

  const createOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        package_id: selectedSlug, accepted_terms: true,
        coupon_code: couponApplied ? coupon : null,
        customer: { ...form },
      };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
      track("package_selected", { package: selectedSlug });
      setStep(3);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  const pay = async (outcome) => {
    setSubmitting(true);
    try {
      await api.post("/payments/mock-complete", { order_id: order.order_id, outcome });
      if (outcome === "success") track("payment_succeeded", { package: selectedSlug });
      nav(`/payment-result?order_id=${order.order_id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const payLive = async () => {
    setSubmitting(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load the payment gateway"); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: "INR",
        name: "Aetheria Numerology",
        description: order.order_id,
        order_id: order.razorpay_order_id,
        prefill: { name: order.customer?.name, email: order.customer?.email },
        theme: { color: "#0B132B" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: order.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            track("payment_succeeded", { package: selectedSlug });
            nav(`/payment-result?order_id=${order.order_id}`);
          } catch (err) { toast.error(apiError(err)); }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-paper">
      <Seo />
      <header className="bg-ivory border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="font-serif text-2xl text-navy">Aetheria</span>
          </Link>
          <Link to="/pricing" className="text-sm text-slate-500 hover:text-navy">Cancel</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        {/* Progress */}
        <div className="flex items-center justify-between mb-10" data-testid="checkout-steps">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${i <= step ? "bg-navy text-ivory" : "bg-white border border-border text-slate-400"}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs mt-2 hidden sm:block ${i <= step ? "text-navy font-medium" : "text-slate-400"}`}>{label}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-navy" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-border p-6 sm:p-10">
          {/* Step 0: Package */}
          {step === 0 && (
            <div data-testid="checkout-step-package">
              <h2 className="font-serif text-3xl text-navy mb-6">Choose your report</h2>
              <div className="space-y-3">
                {packages.map((p) => (
                  <button key={p.slug} data-testid={`checkout-pkg-${p.slug}`} onClick={() => setSelectedSlug(p.slug)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all flex justify-between items-center ${selectedSlug === p.slug ? "border-gold bg-gold-subtle" : "border-border hover:border-slate-300"}`}>
                    <div>
                      <p className="font-serif text-xl text-navy">{p.name}</p>
                      <p className="text-sm text-slate-500">{p.tagline}</p>
                    </div>
                    <span className="font-serif text-2xl text-gold-hover">₹{p.price_inr}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Details */}
          {step === 1 && (
            <div data-testid="checkout-step-details">
              <h2 className="font-serif text-3xl text-navy mb-1">Your details</h2>
              <p className="text-sm text-slate-500 mb-6">Fields marked <span className="text-rose-500">*</span> are required. Your details stay private.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name *"><Input data-testid="field-full-name" value={form.full_name} onChange={set("full_name")} placeholder="As you'd like it to appear" aria-label="Full name" /></Field>
                <Field label="Email *"><Input data-testid="field-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" aria-label="Email" /></Field>
                <Field label="Date of birth *"><Input data-testid="field-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} aria-label="Date of birth" /></Field>
                <Field label="Birth time (optional)"><Input data-testid="field-birth-time" type="time" value={form.birth_time} onChange={set("birth_time")} aria-label="Birth time" /></Field>
                <Field label="Birthplace (optional)"><Input data-testid="field-birth-place" value={form.birth_place} onChange={set("birth_place")} placeholder="City, Country" aria-label="Birthplace" /></Field>
                <Field label="Methodology *">
                  <Select value={form.methodology} onValueChange={(v) => setForm({ ...form, methodology: v })}>
                    <SelectTrigger data-testid="field-methodology" aria-label="Methodology"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pythagorean">Pythagorean</SelectItem>
                      <SelectItem value="chaldean">Chaldean</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {isName && (
                  <>
                    <Field label="Alternative spelling (optional)" full>
                      <Input data-testid="field-alt-spelling" value={form.alternate_spelling} onChange={set("alternate_spelling")} placeholder="e.g. a different spelling of your name" aria-label="Alternative spelling" />
                      <p className="text-xs text-slate-500 mt-1">We will also explore a few sensible spellings when possible. Suggestions are for comparison, not guaranteed improvements.</p>
                    </Field>
                    <Field label="Name comparison focus (optional)" full>
                      <Select value={form.comparison_objective} onValueChange={(v) => setForm({
                        ...form, comparison_objective: v,
                        comparison_target_number: v === "number" ? form.comparison_target_number : null,
                      })}>
                        <SelectTrigger data-testid="field-comparison-objective" aria-label="Name comparison focus"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neutral">No preferred focus</SelectItem>
                          <SelectItem value="career">Career and work reflection</SelectItem>
                          <SelectItem value="relationships">Relationships and communication</SelectItem>
                          <SelectItem value="growth">Personal growth</SelectItem>
                          <SelectItem value="number">Explore a specific number</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    {form.comparison_objective === "number" && (
                      <Field label="Number to explore (1–9, 11, 22, or 33)" full>
                        <Input data-testid="field-comparison-number" type="number" min="1" max="33"
                          value={form.comparison_target_number ?? ""} aria-label="Number to explore"
                          onChange={(e) => setForm({
                            ...form, comparison_target_number: e.target.value ? Number(e.target.value) : null,
                          })} />
                      </Field>
                    )}
                  </>
                )}
                <Field label="Areas of interest (optional)" full>
                  <Textarea data-testid="field-interest" rows={3} value={form.areas_of_interest} onChange={set("areas_of_interest")} placeholder="e.g. career direction, relationships, personal growth" aria-label="Areas of interest" />
                </Field>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && pkg && (
            <div data-testid="checkout-step-review">
              <h2 className="font-serif text-3xl text-navy mb-6">Review your order</h2>
              <div className="bg-paper rounded-xl p-6 space-y-3 text-sm">
                <Row k="Report" v={pkg.name} />
                <Row k="Name" v={form.full_name} />
                <Row k="Date of birth" v={form.date_of_birth} />
                <Row k="Methodology" v={form.methodology} cap />
                {isName && form.comparison_objective !== "neutral" && (
                  <Row k="Comparison focus" v={form.comparison_objective === "number"
                    ? `Number ${form.comparison_target_number || "not selected"}` : form.comparison_objective} cap />
                )}
                <Row k="Delivery estimate" v="~6–7 hours after payment" />
              </div>
              <div className="mt-5 flex gap-3">
                <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code (optional)" aria-label="Coupon code" />
                <button onClick={applyCoupon} className="btn-ghost" data-testid="apply-coupon">Apply</button>
              </div>
              <div className="mt-6 border-t border-border pt-5 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>₹{pkg.price_inr.toFixed(2)}</span></div>
                {discount > 0 && <div className="flex justify-between text-gold-hover"><span>Discount ({couponApplied.percent_off}%)</span><span>−₹{discount.toFixed(2)}</span></div>}
                <div className="flex justify-between font-serif text-2xl text-navy pt-2"><span>Total</span><span data-testid="checkout-total">₹{finalAmount.toFixed(2)}</span></div>
              </div>
              <label className="mt-6 flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="accept-terms" checked={terms} onCheckedChange={setTerms} className="mt-0.5" />
                <span className="text-sm text-slate-600">
                  I accept the <Link to="/terms" target="_blank" className="text-gold-hover underline">Terms</Link>,{" "}
                  <Link to="/refund-policy" target="_blank" className="text-gold-hover underline">Refund Policy</Link>, and{" "}
                  <Link to="/disclaimer" target="_blank" className="text-gold-hover underline">Numerology Disclaimer</Link>. I understand my report is offered for personal reflection.
                </span>
              </label>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && order && (
            <div data-testid="checkout-step-payment" className="text-center">
              <div className="w-14 h-14 rounded-full bg-gold-subtle flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-gold-hover" />
              </div>
              <h2 className="font-serif text-3xl text-navy mb-2">Complete your payment</h2>
              <p className="text-slate-500 mb-1">Order <span className="font-mono-accent">{order.order_id}</span></p>
              <p className="font-serif text-4xl text-navy mb-6">₹{order.amount.toFixed(2)}</p>
              {order.test_mode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 mb-6 text-left">
                  <strong>Demo mode:</strong> This is a simulated checkout. No real money moves.
                </div>
              )}
              {order.gateway_mode === "test" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 mb-6 text-left">
                  <strong>Razorpay test mode:</strong> You will use Razorpay's test checkout. No real money moves.
                </div>
              )}
              {order.test_mode ? (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={() => pay("success")} disabled={submitting} data-testid="pay-success" className="btn-gold">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Pay ₹{order.amount.toFixed(2)}
                  </button>
                  <button onClick={() => pay("failed")} disabled={submitting} data-testid="pay-fail" className="btn-ghost">
                    Simulate failed payment
                  </button>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button onClick={payLive} disabled={submitting} data-testid="pay-razorpay" className="btn-gold">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Pay ₹{order.amount.toFixed(2)} with Razorpay
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Nav buttons */}
          {step < 3 && (
            <div className="flex justify-between mt-10">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                data-testid="checkout-back" className={`btn-ghost ${step === 0 ? "opacity-0 pointer-events-none" : ""}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={next} disabled={submitting} data-testid="checkout-next" className="btn-primary">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {step === 2 ? "Continue to payment" : "Continue"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
function Row({ k, v, cap }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{k}</span>
      <span className={`text-navy font-medium ${cap ? "capitalize" : ""}`}>{v}</span>
    </div>
  );
}

function Seo() {
  useEffect(() => { document.title = "Checkout · Aetheria Numerology"; }, []);
  return null;
}
